// Bluepill Insight design-system tokens. The visualizer (AI) may set a per-answer
// theme + per-block style from VALIDATED vocabularies only — never raw CSS/hex —
// which resolve to CSS custom properties on the `.insight-root` wrapper. Unknown
// values fall back to the Bluepill defaults.

import type { CSSProperties } from 'react';

export type AccentName = 'teal' | 'blue' | 'violet' | 'emerald' | 'amber' | 'rose';
export type Density = 'comfortable' | 'compact';
export type Surface = 'card' | 'flat';

export interface InsightTheme {
  accent?: AccentName;
  density?: Density;
  surface?: Surface;
}

export type Emphasis = 'subtle' | 'normal' | 'strong';
export type Tone = 'default' | 'positive' | 'caution' | 'critical';
export type Size = 'sm' | 'md' | 'lg';

export interface BlockStyle {
  emphasis?: Emphasis;
  tone?: Tone;
  size?: Size;
}

// On-brand accent palette (named → gradient pair). The AI picks a NAME; it cannot
// pass arbitrary hex, so the output is always on-brand.
const ACCENTS: Record<AccentName, { accent: string; accent2: string }> = {
  teal: { accent: '#07E5D1', accent2: '#22D3EE' },
  blue: { accent: '#3B82F6', accent2: '#06B6D4' },
  violet: { accent: '#8B5CF6', accent2: '#6366F1' },
  emerald: { accent: '#10B981', accent2: '#34D399' },
  amber: { accent: '#F59E0B', accent2: '#FBBF24' },
  rose: { accent: '#F43F5E', accent2: '#FB7185' },
};
const DEFAULT_ACCENT: AccentName = 'teal';

const DENSITY: Record<Density, { gap: string; pad: string }> = {
  comfortable: { gap: '1rem', pad: '1.25rem' },
  compact: { gap: '0.625rem', pad: '0.875rem' },
};

const SURFACE: Record<Surface, { card: string; border: string; shadow: string }> = {
  card: {
    card: '#FFFFFF',
    border: 'rgba(148,163,184,0.30)',
    shadow: '0 1px 2px rgba(16,24,40,0.04), 0 4px 14px rgba(16,24,40,0.05)',
  },
  flat: { card: 'transparent', border: 'transparent', shadow: 'none' },
};

const BASE = { ink: '#0F172A', muted: '#94A3B8', bg: '#FFFFFF', radius: '1rem' };

// Tone palette for callouts / emphasized stats (foreground / tint / border).
export const TONES: Record<Tone, { fg: string; bg: string; border: string; label: string }> = {
  default: {
    fg: 'var(--insight-ink)',
    bg: 'var(--insight-bg)',
    border: 'var(--insight-border)',
    label: '',
  },
  positive: { fg: '#0E9384', bg: '#ECFDF5', border: '#6EE7B7', label: 'Positive' },
  caution: { fg: '#B45309', bg: '#FFFBEB', border: '#FCD34D', label: 'Caution' },
  critical: { fg: '#BE123C', bg: '#FFF1F2', border: '#FDA4AF', label: 'Tension' },
};

const ACCENT_NAMES = new Set<string>(Object.keys(ACCENTS));
const TONE_NAMES = new Set<string>(Object.keys(TONES));

export const isAccent = (v: unknown): v is AccentName =>
  typeof v === 'string' && ACCENT_NAMES.has(v);
export const isTone = (v: unknown): v is Tone => typeof v === 'string' && TONE_NAMES.has(v);

/** Resolve a (possibly partial / untrusted) theme to CSS custom properties. */
export function resolveTheme(theme?: InsightTheme | null): CSSProperties {
  const accent = ACCENTS[theme?.accent as AccentName] ?? ACCENTS[DEFAULT_ACCENT];
  const density = DENSITY[theme?.density as Density] ?? DENSITY.comfortable;
  const surface = SURFACE[theme?.surface as Surface] ?? SURFACE.card;
  return {
    '--insight-accent': accent.accent,
    '--insight-accent-2': accent.accent2,
    '--insight-card': surface.card,
    '--insight-border': surface.border,
    '--insight-shadow': surface.shadow,
    '--insight-ink': BASE.ink,
    '--insight-muted': BASE.muted,
    '--insight-bg': BASE.bg,
    '--insight-radius': BASE.radius,
    '--insight-gap': density.gap,
    '--insight-pad': density.pad,
  } as CSSProperties;
}

/** Resolve a block's tone to its color set (default when unknown/absent). */
export const resolveTone = (style?: BlockStyle | null) =>
  TONES[isTone(style?.tone) ? (style!.tone as Tone) : 'default'];

/** Serialize a resolved theme to a CSS declaration string for `:root` (trusted
 *  values only — comes from resolveTheme's fixed maps, never raw LLM input). */
export function themeToCss(theme?: InsightTheme | null): string {
  const vars = resolveTheme(theme) as Record<string, string>;
  return Object.entries(vars)
    .map(([k, v]) => `${k}:${v}`)
    .join(';');
}
